import API_BASE from '../config';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = API_BASE;

/**
 * Lands here from the WhatsApp payment_abandoned button.
 * Fetches the saved checkout state (customer + amount + coupon) and hands
 * control to /checkout with prefill params so the customer can pick up
 * exactly where they left off.
 */
const CheckoutResumePage = () => {
  const { txId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | ready | already_paid | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/checkout/resume/${encodeURIComponent(txId)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data.detail || 'Could not load your checkout session.');

        if (data.already_paid) {
          setStatus('already_paid');
          setTimeout(() => {
            navigate(`/payment-success?order_id=${encodeURIComponent(data.order_id || '')}&provider=razorpay`, { replace: true });
          }, 1500);
          return;
        }

        // Prefill /checkout via query params and jump straight there.
        const q = new URLSearchParams();
        if (data.customer_name)    q.set('name',    data.customer_name);
        if (data.customer_email)   q.set('email',   data.customer_email);
        if (data.customer_phone)   q.set('phone',   data.customer_phone);
        if (data.customer_country) q.set('country', data.customer_country);
        if (data.customer_state)   q.set('state',   data.customer_state);
        if (data.coupon_code)      q.set('coupon',  data.coupon_code);
        navigate(`/checkout?${q.toString()}`, { replace: true });
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.message || 'Something went wrong.');
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [txId, navigate]);

  const box = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e8f1fc 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  };
  const card = {
    background: '#fff', borderRadius: 20, padding: '48px 40px',
    boxShadow: '0 20px 60px rgba(26,111,214,0.15)',
    maxWidth: 460, width: '100%', textAlign: 'center',
  };

  // Brand logo, clickable home link — shown above the card in every state.
  // This page usually redirects within ~0-1.5s (loading/already_paid), but
  // the error state has no auto-redirect, so without this a customer whose
  // resume link failed had no way out except a manual URL edit or reload.
  const logo = (
    <div
      style={{ marginBottom: 24, cursor: 'pointer', display: 'inline-flex' }}
      onClick={() => navigate('/')}
    >
      <img src="/images/brand/successlane-logo-mark.png" alt="SuccessLane" style={{ height: 22, width: 'auto', display: 'block' }} />
    </div>
  );

  if (status === 'loading') {
    return (
      <div style={box}>
        <div style={{ textAlign: 'center' }}>
          {logo}
          <div style={card}>
            <Loader2 style={{ width: 48, height: 48, color: '#1a6fd6', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
              Picking up where you left off…
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
              One moment — we're loading your checkout details.
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'already_paid') {
    return (
      <div style={box}>
        <div style={{ textAlign: 'center' }}>
          {logo}
          <div style={card}>
            <CheckCircle style={{ width: 48, height: 48, color: '#059669', margin: '0 auto 20px' }} />
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
              You're all set!
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
              This payment was already completed. Taking you to your confirmation page…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ textAlign: 'center' }}>
        {logo}
        <div style={card}>
          <XCircle style={{ width: 48, height: 48, color: '#dc2626', margin: '0 auto 20px' }} />
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
            Session not found
          </h2>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6, marginBottom: 20 }}>
            {errorMsg || "We couldn't find this checkout. Please start a new order."}
          </p>
          <button
            type="button"
            onClick={() => navigate('/checkout', { replace: true })}
            style={{
              padding: '12px 24px', background: '#1a6fd6', color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Start new order
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutResumePage;
